import { type SpeechEngine, VOICE_TRANSCRIBE_MAX_SECONDS } from '@fos/shared/contracts/speech';
import { useCallback, useEffect, useRef, useState } from 'react';
import { recorderSupported, useAudioRecorder } from './recorder';
import { SPEECH_MESSAGES, speechRecognitionCtor, useSpeechRecognition } from './speech';

/**
 * Dyktowanie (Fala 10, tor bubble-voice) — mechanizm mikrofonu czatu Copilota (bąbel AI i od 10.10.2026 mikrofon
 * dolnego menu na telefonie, który otwiera ten sam czat). Silnik z Ustawień (AI i pamięć → Mowa na tekst):
 *  - `browser` — Web Speech API pl-PL (transkrypcja na żywo, do aplikacji trafia tylko tekst),
 *  - `openai` — nagranie (MediaRecorder) → serwer → OpenAI; bez transkrypcji na serwerze (brak klucza, AI wyłączone) —
 *    przeglądarka z komunikatem,
 *  - `auto` — przeglądarka, a bez niej (Firefox, część Safari) albo przy awarii usługi mowy przeglądarki — serwer.
 * Odmowa mikrofonu albo brak obu silników nie blokuje pisania (pole tekstowe zawsze działa).
 */

export type DictationEngine = 'browser' | 'server';
export type DictationStatus = 'idle' | 'listening' | 'recording' | 'transcribing' | 'unsupported' | 'denied' | 'error';

/** Wybór silnika (czysta funkcja — testy). */
export function pickDictationEngine(o: {
  engine: SpeechEngine;
  browserSupported: boolean;
  serverAvailable: boolean;
  recorderSupported: boolean;
  /** usługa mowy przeglądarki zawiodła w tej sesji (np. brak sieci do usługi Google) */
  browserFailed?: boolean;
}): DictationEngine | null {
  const server = o.serverAvailable && o.recorderSupported;
  if (o.engine === 'browser') return o.browserSupported ? 'browser' : null;
  if (o.engine === 'openai') return server ? 'server' : o.browserSupported ? 'browser' : null;
  if (o.browserSupported && !o.browserFailed) return 'browser';
  if (server) return 'server';
  return o.browserSupported ? 'browser' : null;
}

export interface DictationOptions {
  lang?: string;
  /** silnik z `voice.speechStatus` (brak = automatycznie) */
  engine?: SpeechEngine;
  /** transkrypcja na serwerze dostępna (`voice.speechStatus.server.available`) */
  serverAvailable?: boolean;
  /** powód niedostępności transkrypcji na serwerze (komunikat przy silniku „OpenAI”) */
  serverMessage?: string | null;
  /** koniec mówienia po ciszy (opcja „Wyślij po zakończeniu mówienia”) */
  autoFinish?: boolean;
  /** zakończone dyktowanie z tekstem (cisza, „Zakończ”, puszczenie przycisku, tekst z serwera) */
  onFinished?(text: string): void;
}

export interface DictationState {
  /** silnik, który zostanie użyty przy następnym `start` (albo jest w użyciu) */
  engine: DictationEngine | null;
  supported: boolean;
  status: DictationStatus;
  /** tekst rozpoznany (z tekstem bazowym z `start(base)`) */
  finalText: string;
  /** tekst w trakcie rozpoznawania (tylko przeglądarka) */
  interimText: string;
  error: string | null;
  /** informacja (np. przejście na OpenAI po awarii przeglądarki, silnik „OpenAI” niedostępny) */
  notice: string | null;
  /** sekundy nagrania (serwer) */
  elapsed: number;
  maxSeconds: number;
  /** ostatni tekst z atrapy transkrypcji (symulacja) */
  simulated: boolean;
  active: boolean;
  start(base?: string): void;
  stop(): void;
  cancel(): void;
  reset(): void;
}

const SILENCE_MS = 1600;

function join(base: string, text: string): string {
  return [base.trim(), text.trim()].filter(Boolean).join(' ');
}

export function useDictation(opts: DictationOptions = {}): DictationState {
  const speech = useSpeechRecognition({ lang: opts.lang ?? 'pl-PL' });
  const [browserSupported] = useState(() => speechRecognitionCtor() !== null);
  const [canRecord] = useState(recorderSupported);
  const [browserFailed, setBrowserFailed] = useState(false);
  const [serverText, setServerText] = useState('');
  const [simulated, setSimulated] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [active, setActive] = useState<DictationEngine | null>(null);
  const baseRef = useRef('');
  const finishRef = useRef(false);
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const engineSetting = opts.engine ?? 'auto';
  const serverAvailable = opts.serverAvailable ?? false;
  const engine = pickDictationEngine({
    engine: engineSetting,
    browserSupported,
    serverAvailable,
    recorderSupported: canRecord,
    browserFailed,
  });

  const recorder = useAudioRecorder({
    maxSeconds: VOICE_TRANSCRIBE_MAX_SECONDS,
    stopOnSilence: opts.autoFinish,
    onText(text, meta) {
      const joined = join(baseRef.current, text);
      setServerText(joined);
      setSimulated(meta.simulated);
      optsRef.current.onFinished?.(joined);
    },
  });

  const startServer = useCallback(
    (base: string) => {
      baseRef.current = base;
      setServerText(base.trim());
      setActive('server');
      void recorder.start();
    },
    [recorder.start],
  );

  const start = useCallback(
    (base = '') => {
      setNotice(engineSetting === 'openai' && engine === 'browser' ? (opts.serverMessage ?? null) : null);
      finishRef.current = false;
      if (engine === 'server') {
        startServer(base);
        return;
      }
      if (engine === 'browser') {
        baseRef.current = base;
        setActive('browser');
        speech.start(base);
        return;
      }
      setActive(null);
    },
    [engine, engineSetting, opts.serverMessage, speech.start, startServer],
  );

  const stop = useCallback(() => {
    finishRef.current = true;
    if (active === 'server') recorder.stop();
    else speech.stop();
  }, [active, recorder.stop, speech.stop]);

  const cancel = useCallback(() => {
    finishRef.current = false;
    recorder.cancel();
    speech.reset();
  }, [recorder.cancel, speech.reset]);

  const reset = useCallback(() => {
    finishRef.current = false;
    setServerText('');
    setNotice(null);
    recorder.reset();
    speech.reset();
  }, [recorder.reset, speech.reset]);

  // Automatycznie: usługa mowy przeglądarki bez sieci → nagranie i transkrypcja na serwerze (jeśli dostępna)
  // biome-ignore lint/correctness/useExhaustiveDependencies: reakcja na błąd rozpoznawania
  useEffect(() => {
    if (active !== 'browser' || speech.status !== 'error' || speech.error !== SPEECH_MESSAGES.network) return;
    if (engineSetting !== 'auto' || !serverAvailable || !canRecord) return;
    setBrowserFailed(true);
    setNotice('Rozpoznawanie mowy w przeglądarce nie działa — nagrywam, tekst przepisze OpenAI.');
    startServer(speech.finalText || baseRef.current);
  }, [speech.status, speech.error]);

  // koniec mówienia po ciszy (przeglądarka): tekst bez zmian przez 1,6 s = koniec
  const listening = active === 'browser' && speech.status === 'listening';
  useEffect(() => {
    if (!listening || !optsRef.current.autoFinish) return;
    const heard = Boolean(speech.interimText || speech.finalText.trim() !== baseRef.current.trim());
    if (!heard) return;
    const timer = window.setTimeout(() => {
      finishRef.current = true;
      speech.stop();
    }, SILENCE_MS);
    return () => window.clearTimeout(timer);
  }, [listening, speech.finalText, speech.interimText, speech.stop]);

  // zakończone słuchanie przeglądarki (Zakończ, cisza, koniec po stronie usługi) = gotowy tekst
  const wasListening = useRef(false);
  useEffect(() => {
    if (listening) {
      wasListening.current = true;
      return;
    }
    if (!wasListening.current) return;
    wasListening.current = false;
    if (speech.status !== 'idle') return;
    const text = speech.finalText.trim();
    const spoke = text !== baseRef.current.trim();
    if ((finishRef.current || optsRef.current.autoFinish) && spoke && text) optsRef.current.onFinished?.(text);
    finishRef.current = false;
  }, [listening, speech.status, speech.finalText]);

  let status: DictationStatus;
  if (active === 'server') {
    status = recorder.status === 'idle' ? 'idle' : recorder.status;
  } else if (active === 'browser' || engine === 'browser') {
    status = speech.status;
  } else {
    status = engine === null ? 'unsupported' : 'idle';
  }

  return {
    engine,
    supported: engine !== null,
    status,
    finalText: active === 'server' ? serverText : speech.finalText,
    interimText: active === 'server' ? '' : speech.interimText,
    error: active === 'server' ? recorder.error : speech.error,
    notice,
    elapsed: recorder.elapsed,
    maxSeconds: VOICE_TRANSCRIBE_MAX_SECONDS,
    simulated,
    active: status === 'listening' || status === 'recording' || status === 'transcribing',
    start,
    stop,
    cancel,
    reset,
  };
}

/** „0:12” — licznik nagrania. */
export function formatElapsed(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
